import React from 'react';
import mashwaraImg from '../assets/mashwara.png';
import liturgyImg from '../assets/liturgy.png';
import communicationImg from '../assets/communication2.png';
import educationImg from '../assets/education.jpg';

const Work = () => {
  return (
    <div className="min-h-screen bg-[#EBDCCD] py-16 px-4 sm:px-6 lg:px-8 font-sans text-[#2C1A14]">
      <div className="max-w-6xl mx-auto space-y-16">
        
        {/* PAGE HEADER */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#2C1A14] bg-[#D4B896]/30 px-4 py-1.5 rounded-full border border-[#2C1A14]/10 inline-block">
            Our Key Initiatives
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold tracking-tight text-[#2C1A14]">
            How We Serve & Connect
          </h1>
          <p className="text-stone-700 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            From bi-weekly family governance to youth skill development, academic support, and monthly cultural observances, explore the structured programs driving our trust forward.
          </p>
        </div>

        {/* SECTION 1: MASHWARA */}
        <div className="bg-white/70 backdrop-blur-md p-6 sm:p-10 rounded-3xl border border-[#2C1A14]/15 shadow-xl hover:shadow-2xl transition-all duration-300">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            
            {/* Content */}
            <div className="flex-1 space-y-5">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-[#2C1A14] text-[#D4B896] text-xs font-bold uppercase tracking-wider rounded-lg shadow-sm">
                  Bi-Weekly • Sundays
                </span>
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-widest">
                  Governance & Welfare
                </span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C1A14]">
                Mashwara
              </h2>

              <p className="text-stone-700 leading-relaxed text-sm sm:text-base border-l-2 border-[#D4B896] pl-4">
                Mashwara is the core decision-making mechanism adopted by our trust. Every alternate Sunday, we gather to hold open discussions regarding family welfare, property maintenance, educational guidance, health, and cultural events.
              </p>

              {/* Feature Chips */}
              <div className="pt-2 flex flex-wrap gap-2">
                {['Property Maintenance', 'Education & Health', 'Operating Manual Process', 'Cultural Planning'].map((tag, idx) => (
                  <span key={idx} className="text-xs bg-[#2C1A14]/5 text-[#2C1A14] border border-[#2C1A14]/15 px-3 py-1 rounded-md font-medium">
                    ✓ {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Visual Frame */}
            <div className="flex-1 w-full group">
              <div className="relative overflow-hidden rounded-2xl border-2 border-[#2C1A14]/20 shadow-2xl bg-[#2C1A14]">
                <img
                  src={mashwaraImg}
                  alt="Mashwara Meeting"
                  className="w-full h-72 sm:h-80 object-cover opacity-95 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C1A14]/60 via-transparent to-transparent opacity-80" />
              </div>
            </div>

          </div>
        </div>

        {/* SECTION 2: COMMUNICATION & CONFIDENCE */}
        <div className="bg-white/70 backdrop-blur-md p-6 sm:p-10 rounded-3xl border border-[#2C1A14]/15 shadow-xl hover:shadow-2xl transition-all duration-300">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-10">
            
            {/* Content */}
            <div className="flex-1 space-y-5">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-[#2C1A14] text-[#D4B896] text-xs font-bold uppercase tracking-wider rounded-lg shadow-sm">
                  Ongoing Program
                </span>
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-widest">
                  Youth & Skill Development
                </span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C1A14]">
                Communication Skill Training
              </h2>

              <p className="text-stone-700 leading-relaxed text-sm sm:text-base border-l-2 border-[#D4B896] pl-4">
                Communication is both an art and a practical craft. This initiative provides specialized vocabulary, vocal confidence, and reasoning workshops, connecting students with industry professionals across medical, legal, and commercial fields through interactive "Feel Free" exchange modules.
              </p>

              {/* Feature Grid Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="p-3 rounded-xl bg-[#2C1A14]/5 border border-[#2C1A14]/10">
                  <p className="font-bold text-xs text-[#2C1A14] uppercase tracking-wider">Industry Guidance</p>
                  <p className="text-xs text-stone-600 mt-1">Direct access to medical and legal professionals.</p>
                </div>
                <div className="p-3 rounded-xl bg-[#2C1A14]/5 border border-[#2C1A14]/10">
                  <p className="font-bold text-xs text-[#2C1A14] uppercase tracking-wider">Vocal Confidence</p>
                  <p className="text-xs text-stone-600 mt-1">Targeted feedback to refine reasoning & presentation.</p>
                </div>
              </div>
            </div>

            {/* Visual Frame */}
            <div className="flex-1 w-full group">
              <div className="relative overflow-hidden rounded-2xl border-2 border-[#2C1A14]/20 shadow-2xl bg-[#2C1A14]">
                <img
                  src={communicationImg}
                  alt="Communication and Confidence Training"
                  className="w-full h-72 sm:h-80 object-cover opacity-95 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C1A14]/60 via-transparent to-transparent opacity-80" />
              </div>
            </div>

          </div>
        </div>

        {/* SECTION 3: EDUCATION */}
        <div className="bg-white/70 backdrop-blur-md p-6 sm:p-10 rounded-3xl border border-[#2C1A14]/15 shadow-xl hover:shadow-2xl transition-all duration-300">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            
            {/* Content */}
            <div className="flex-1 space-y-5">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-[#2C1A14] text-[#D4B896] text-xs font-bold uppercase tracking-wider rounded-lg shadow-sm">
                  Continuous Support
                </span>
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-widest">
                  Academic Advancement
                </span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C1A14]">
                Education Initiatives
              </h2>

              <p className="text-stone-700 leading-relaxed text-sm sm:text-base border-l-2 border-[#D4B896] pl-4">
                Education is the primary bedrock for long-term empowerment. Our trust actively supports students through academic resources, mentorship networks, and educational welfare grants to ensure every young member has the foundation they need to succeed.
              </p>

              {/* Feature Chips */}
              <div className="pt-2 flex flex-wrap gap-2">
                {['Academic Mentorship', 'Educational Resources', 'Student Welfare Grants', 'Skill Building'].map((tag, idx) => (
                  <span key={idx} className="text-xs bg-[#2C1A14]/5 text-[#2C1A14] border border-[#2C1A14]/15 px-3 py-1 rounded-md font-medium">
                    ✓ {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Visual Frame */}
            <div className="flex-1 w-full group">
              <div className="relative overflow-hidden rounded-2xl border-2 border-[#2C1A14]/20 shadow-2xl bg-[#2C1A14]">
                <img
                  src={educationImg}
                  alt="Education Initiatives"
                  className="w-full h-72 sm:h-80 object-cover opacity-95 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C1A14]/60 via-transparent to-transparent opacity-80" />
              </div>
            </div>

          </div>
        </div>

        {/* SECTION 4: LITURGY */}
        <div className="bg-white/70 backdrop-blur-md p-6 sm:p-10 rounded-3xl border border-[#2C1A14]/15 shadow-xl hover:shadow-2xl transition-all duration-300">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-10">
            
            {/* Content */}
            <div className="flex-1 space-y-5">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-[#2C1A14] text-[#D4B896] text-xs font-bold uppercase tracking-wider rounded-lg shadow-sm">
                  1st Friday • Monthly
                </span>
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-widest">
                  Tradition & Fellowship
                </span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C1A14]">
                Liturgy & Ceremony
              </h2>

              <p className="text-stone-700 leading-relaxed text-sm sm:text-base border-l-2 border-[#D4B896] pl-4">
                On the first Friday of every month, a dedicated family Fateha is observed. Beyond spiritual remembrance, this regular assembly reinforces togetherness by sharing meals and sweets across generations.
              </p>

              {/* Feature Chips */}
              <div className="pt-2 flex flex-wrap gap-2">
                {['Monthly Fateha', 'Community Meal Sharing', 'Cultural Heritage', 'Generational Connection'].map((tag, idx) => (
                  <span key={idx} className="text-xs bg-[#2C1A14]/5 text-[#2C1A14] border border-[#2C1A14]/15 px-3 py-1 rounded-md font-medium">
                    ✓ {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Visual Frame */}
            <div className="flex-1 w-full group">
              <div className="relative overflow-hidden rounded-2xl border-2 border-[#2C1A14]/20 shadow-2xl bg-[#2C1A14]">
                <img
                  src={liturgyImg}
                  alt="Liturgy Ceremony"
                  className="w-full h-72 sm:h-80 object-cover opacity-95 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C1A14]/60 via-transparent to-transparent opacity-80" />
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default Work;